/**
 * Which candles an indicator computes on.
 *
 * An indicator's SOURCE and the chart's DISPLAY candle type are two different
 * decisions, and conflating them is a bug: switch the chart to Heikin-Ashi to
 * read the trend and every oscillator silently re-computes on synthetic prices,
 * so the levels move and marks appear or vanish for a reason that has nothing to
 * do with the market. TradingChart already states this intent for signal
 * markers - "computed on raw OHLC so positions & values are stable across
 * display candle types" - but the BBR/Wave oscillator block was reading the
 * transformed series, which is exactly the drift this module removes.
 *
 * Every source is derived from RAW candles, so the answer never depends on what
 * the chart happens to be drawing.
 *
 * PORTED to backend/src/services/strategies/chartPort/indicatorSource.ts. That
 * copy is a verbatim port with CandleRow declared locally (the chartPort
 * convention), NOT a byte-identical twin, so it is checked by EXECUTION in
 * bigmove/parity.ts rather than by sha.
 */
import { CandleRow } from '../components/charts/TradingChart';
import { transformCandles } from './candleTransforms';
import { computeClnBars, CLN_DEFAULT_LENGTH } from './clnCandles';

export type IndicatorSource = 'Regular' | 'HA' | 'CLH' | 'CLN';

export const INDICATOR_SOURCES: Array<{ value: IndicatorSource; label: string; title: string }> = [
  { value: 'Regular', label: 'Reg', title: 'Real market OHLC. What the research was measured on.' },
  { value: 'HA', label: 'HA', title: 'Smoothed Heikin-Ashi bodies — fewer flips, but synthetic prices.' },
  { value: 'CLH', label: 'CLH', title: 'Custom Linreg HA — the smoothest of the bar transforms.' },
  { value: 'CLN', label: 'CLN', title: 'Linear-regression bodies, WICKLESS — the strongest smoothing.' },
];

/**
 * Build the candle series an indicator should compute on.
 *
 * `raw` must be RAW market candles, not the chart's display series.
 *
 * CLN is the one that needs explaining. It is not a bar transform - on the
 * chart it is a second layer painted over the real candle, so
 * `transformCandles(x, 'CLN')` is deliberately a passthrough. As an indicator
 * SOURCE it means the regression body: open/close are the regression values and
 * high/low are the BODY extremes, i.e. wickless. That matters more than it
 * sounds, because BBR and WaveTrend both feed on HLC3 - dropping the wicks
 * removes the single noisiest input they have, which is why CLN and CLH quiet
 * the oscillators down.
 *
 * Length is only read for CLN; the other sources ignore it.
 */
export function indicatorSourceCandles(
  raw: CandleRow[],
  type: IndicatorSource,
  clnLength: number = CLN_DEFAULT_LENGTH,
): CandleRow[] {
  if (!raw || raw.length === 0) return raw || [];
  switch (type) {
    case 'HA':
    case 'CLH':
      return transformCandles(raw, type);
    case 'CLN': {
      const bars = computeClnBars(raw, clnLength);
      const out: CandleRow[] = new Array(raw.length);
      for (let i = 0; i < raw.length; i++) {
        const b = bars[i];
        // A bar the regression could not fit falls back to the real candle
        // rather than to a hole: a null here would propagate into the
        // oscillator warm-up and silently shorten every series downstream.
        out[i] = b
          ? {
            time: raw[i].time,
            open: b.lrOpen,
            high: b.lrBodyHigh,
            low: b.lrBodyLow,
            close: b.lrClose,
            volume: raw[i].volume,
          }
          : raw[i];
      }
      return out;
    }
    case 'Regular':
    default:
      return raw;
  }
}
